// src/components/ResumeView.js
//
// Visualização imprimível do currículo (Fase G.1.3). É renderizada na
// página de detalhe pública (`/curriculos/:userId`) e também usada como
// fallback para download em PDF via `window.print()`.
import React from 'react';
import H2 from './H2/H2';
import H3 from './H3/H3';
import {
  CATEGORY_LABELS,
  EDUCATION_LEVELS,
  LANGUAGE_LEVELS,
  AVAILABILITY_TYPES,
} from '../utils/resumeConstants';

const formatMonth = (value) => {
  if (!value) return '';
  const [year, month] = value.split('-');
  if (!year || !month) return value;
  return `${month}/${year}`;
};

const ResumeView = ({ resume }) => {
  if (!resume) return null;

  return (
    <article className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 print:shadow-none print:border-0 print:rounded-none print:p-0 max-w-3xl mx-auto">
      <header className="mb-6 border-b border-gray-200 pb-4">
        <H2 text={resume.fullName || 'Currículo'} textColor="purple" />
        {resume.headline && <p className="text-base text-gray-700 -mt-3">{resume.headline}</p>}
        <div className="mt-2 text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
          {resume.email && <span>{resume.email}</span>}
          {resume.phone && <span>{resume.phone}</span>}
          {(resume.city || resume.state) && <span>{[resume.city, resume.state, resume.country].filter(Boolean).join(', ')}</span>}
          {resume.nationality && <span>Nacionalidade: {resume.nationality}</span>}
        </div>
        {resume.category && (
          <p className="mt-1 text-xs uppercase tracking-wide font-bold text-orange-600">
            Área: {CATEGORY_LABELS[resume.category] || resume.category}
          </p>
        )}
      </header>

      {resume.about && (
        <section className="mb-6">
          <H3 text="Sobre" textColor="purple" />
          <p className="text-sm text-gray-700 -mt-3 whitespace-pre-line">{resume.about}</p>
        </section>
      )}

      {Array.isArray(resume.experience) && resume.experience.length > 0 && (
        <section className="mb-6">
          <H3 text="Experiência profissional" textColor="purple" />
          <ul className="-mt-2 space-y-3">
            {resume.experience.map((exp, idx) => (
              <li key={idx}>
                <p className="font-semibold text-sm text-gray-800">{exp.role || ''}{exp.company ? ` — ${exp.company}` : ''}</p>
                <p className="text-xs text-gray-500">
                  {formatMonth(exp.startMonth)}{' — '}{exp.current ? 'Atual' : formatMonth(exp.endMonth) || ''}
                </p>
                {exp.description && <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{exp.description}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(resume.education) && resume.education.length > 0 && (
        <section className="mb-6">
          <H3 text="Formação acadêmica" textColor="purple" />
          <ul className="-mt-2 space-y-3">
            {resume.education.map((edu, idx) => (
              <li key={idx}>
                <p className="font-semibold text-sm text-gray-800">{edu.course || ''}{edu.level ? ` — ${EDUCATION_LEVELS[edu.level] || edu.level}` : ''}</p>
                <p className="text-xs text-gray-500">{edu.institution || ''}{(edu.startYear || edu.endYear) ? ` · ${edu.startYear || ''}${edu.endYear ? ` - ${edu.endYear}` : ''}` : ''}</p>
                {edu.description && <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{edu.description}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(resume.languages) && resume.languages.length > 0 && (
        <section className="mb-6">
          <H3 text="Idiomas" textColor="purple" />
          <ul className="-mt-2 flex flex-wrap gap-2">
            {resume.languages.map((lang, idx) => (
              <li key={idx} className="text-sm text-gray-700 bg-gray-100 rounded-full px-3 py-1">
                {lang.name}{lang.level ? ` · ${LANGUAGE_LEVELS[lang.level] || lang.level}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(resume.skills) && resume.skills.length > 0 && (
        <section className="mb-6">
          <H3 text="Habilidades" textColor="purple" />
          <div className="-mt-2 flex flex-wrap gap-2">
            {resume.skills.map((skill) => (
              <span key={skill} className="text-sm text-gray-700 bg-gray-100 rounded-full px-3 py-1">{skill}</span>
            ))}
          </div>
        </section>
      )}

      {resume.availability && ((Array.isArray(resume.availability.types) && resume.availability.types.length > 0) || resume.availability.immediate || resume.availability.remote) && (
        <section className="mb-6">
          <H3 text="Disponibilidade" textColor="purple" />
          <div className="-mt-2 flex flex-wrap gap-2 text-sm text-gray-700">
            {Array.isArray(resume.availability.types) && resume.availability.types.map((t) => (
              <span key={t} className="bg-gray-100 rounded-full px-3 py-1">{AVAILABILITY_TYPES[t] || t}</span>
            ))}
            {resume.availability.immediate && <span className="bg-green-100 text-green-800 rounded-full px-3 py-1">Disponibilidade imediata</span>}
            {resume.availability.remote && <span className="bg-blue-100 text-blue-800 rounded-full px-3 py-1">Remoto</span>}
          </div>
        </section>
      )}

      {resume.attachmentUrl && (
        <section className="mb-2 print:hidden">
          <H3 text="Anexo" textColor="purple" />
          <a href={resume.attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline">
            Baixar {resume.attachmentName || 'currículo em PDF'}
          </a>
        </section>
      )}
    </article>
  );
};

export default ResumeView;
